// src/components/assistant/PersonalizationPanel.tsx
//
// Panel de Personalización (estilo ChatGPT, más simple): el usuario define cómo
// quiere que le hablen (estilo/tono) + instrucciones libres + contexto propio.
// Se guarda en GET/PUT /me/preferences (backend Slice C) y el cerebro lo inyecta
// en el system prompt.
//
// Degrada bien: si el endpoint aún no está deployado, muestra el formulario vacío
// y avisa al guardar (no rompe la interfaz).

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { apiUrl } from '@/config';

interface PersonalizationPanelProps {
  webChatHeaders?: Record<string, string>;
  onClose: () => void;
}

const STYLES = [
  { value: '', label: 'Automático' },
  { value: 'amigable', label: 'Amigable' },
  { value: 'formal', label: 'Formal' },
  { value: 'conciso', label: 'Conciso' },
  { value: 'directo', label: 'Directo' },
  { value: 'didactico', label: 'Didáctico' },
];

export function PersonalizationPanel({ webChatHeaders = {}, onClose }: PersonalizationPanelProps) {
  const [customInstructions, setCustomInstructions] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [style, setStyle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const headers = (): Record<string, string> => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    ...webChatHeaders,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/me/preferences'), { headers: headers() });
        if (!res.ok) throw new Error();
        const data = await res.json();
        const p = data?.preferences || {};
        if (cancelled) return;
        setCustomInstructions(p.custom_instructions || '');
        setAboutMe(p.about_me || '');
        setStyle(p.response_style || '');
      } catch {
        // endpoint aún no deployado → formulario vacío
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(apiUrl('/me/preferences'), {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({
          custom_instructions: customInstructions,
          about_me: aboutMe,
          response_style: style,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setMsg('Guardado ✓');
      setTimeout(onClose, 700);
    } catch {
      setMsg('No se pudo guardar (¿backend actualizado?).');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Personalización</h2>
            <p className="text-sm text-muted-foreground">
              Define cómo quieres que te hable el asistente. Aplica a todas tus conversaciones.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Estilo / tono</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              >
                {STYLES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                ¿Qué debería saber el asistente sobre ti?
              </label>
              <textarea
                value={aboutMe}
                onChange={(e) => setAboutMe(e.target.value)}
                rows={2}
                placeholder="Tu rol, tu empresa, cómo prefieres trabajar…"
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Instrucciones personalizadas
              </label>
              <textarea
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                rows={4}
                placeholder="Cómo quieres que responda: formato, idioma, nivel de detalle, qué evitar…"
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-muted-foreground">{msg}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-muted"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonalizationPanel;
